import { useState, useEffect, useRef, useMemo, useCallback } from 'react';
import { getRankImage, getRoleImage, getRankValue } from '../common/consts';
import useStore from '../store/store';
import './Players.css';
import {
  BarChart,
  Bar,
  LineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer,
} from 'recharts';

// Search and sort controls component - moved outside to prevent re-creation on each render
const SearchSortControls = ({ searchQuery, setSearchQuery, sortOption, setSortOption, activeView }) => {
  return (
    <div className="search-sort-controls">
      <div className="search-container">
        <input
          type="text"
          className="search-input"
          placeholder="Search player/team"
          value={searchQuery}
          onChange={(e) => setSearchQuery(e.target.value)}
        />
        <div className="search-icon">
          <svg
            xmlns="http://www.w3.org/2000/svg"
            width="24"
            height="24"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth="2"
            strokeLinecap="round"
            strokeLinejoin="round"
          >
            <circle cx="11" cy="11" r="8"></circle>
            <line x1="21" y1="21" x2="16.65" y2="16.65"></line>
          </svg>
        </div>
      </div>
      {activeView === 'players' && (
        <div className="sort-container">
          <select className="sort-select" value={sortOption} onChange={(e) => setSortOption(e.target.value)}>
            <option value="name">Name</option>
            <option value="rank">Rank</option>
          </select>
          <div className="sort-icon">
            <svg
              xmlns="http://www.w3.org/2000/svg"
              width="24"
              height="24"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="2"
              strokeLinecap="round"
              strokeLinejoin="round"
            >
              <polyline points="6 9 12 15 18 9"></polyline>
            </svg>
          </div>
        </div>
      )}
    </div>
  );
};

function Players() {
  const [activeSeason, setActiveSeason] = useState(null);
  const [activeView, setActiveView] = useState(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [sortOption, setSortOption] = useState('name');
  const originalBodyClass = useRef('');
  const [seasons, setSeasons] = useState([]);
  const [seasonPlayers, setSeasonPlayers] = useState([]);
  const [seasonTeams, setSeasonTeams] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [statsData, setStatsData] = useState({});
  const [statsLoading, setStatsLoading] = useState(false);
  const [statsSortField, setStatsSortField] = useState('kills');
  const [statsSortDirection, setStatsSortDirection] = useState('desc');
  const [viewMode, setViewMode] = useState('detailed'); // 'compact' or 'detailed'
  const [isMobileView, setIsMobileView] = useState(false);
  const [touchStart, setTouchStart] = useState(null);
  const [touchEnd, setTouchEnd] = useState(null);
  const [selectedTeam, setSelectedTeam] = useState('all');
  const [selectedRole, setSelectedRole] = useState('all');
  const [selectedRank, setSelectedRank] = useState('all');
  const [selectedVerified, setSelectedVerified] = useState('all');
  const [showFilters, setShowFilters] = useState(false);
  const [hoveredPlayer, setHoveredPlayer] = useState(null);
  const [popoverPosition, setPopoverPosition] = useState({ x: 0, y: 0 });
  const [fixtures, setFixtures] = useState([]);
  const [chartMetric, setChartMetric] = useState('kills'); // kills, kd, assists
  const [chartPlayerCount, setChartPlayerCount] = useState(10);
  const [showCharts, setShowCharts] = useState(false);
  const [performanceMetric, setPerformanceMetric] = useState('all'); // all, kills, deaths, assists
  const [teamMetric, setTeamMetric] = useState('all'); // all, kills, deaths, assists
  const [showBackToTop, setShowBackToTop] = useState(false);
  const seasonTabsRef = useRef(null);

  // Player performance table pagination + control
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize] = useState(10);

  // Reset to page 1 when filters/sort change
  useEffect(() => {
    setCurrentPage(1);
  }, [searchQuery, selectedTeam, statsSortField, statsSortDirection]);

  useEffect(() => {
    // Store original body class and add no-bg class when Players component mounts
    const bodyElement = document.querySelector('body');
    originalBodyClass.current = bodyElement.className;
    bodyElement.className = 'no-bg';

    // Restore original body class when component unmounts
    return () => {
      bodyElement.className = originalBodyClass.current;
    };
  }, []);

  // Detect mobile view
  useEffect(() => {
    const checkMobileView = () => {
      setIsMobileView(window.innerWidth <= 768);
    };
    
    checkMobileView();
    window.addEventListener('resize', checkMobileView);
    
    return () => window.removeEventListener('resize', checkMobileView);
  }, []);

  // Scroll handler for back to top button
  useEffect(() => {
    const handleScroll = () => {
      setShowBackToTop(window.pageYOffset > 300);
    };

    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const scrollToTop = () => {
    window.scrollTo({
      top: 0,
      behavior: 'smooth'
    });
  };

  // Swipe handlers for mobile navigation
  const minSwipeDistance = 50;

  const onTouchStart = (e) => {
    setTouchEnd(null);
    setTouchStart(e.targetTouches[0].clientX);
  };

  const onTouchMove = (e) => {
    setTouchEnd(e.targetTouches[0].clientX);
  };

  const onTouchEnd = () => {
    if (!touchStart || !touchEnd) return;
    
    const distance = touchStart - touchEnd;
    const isLeftSwipe = distance > minSwipeDistance;
    const isRightSwipe = distance < -minSwipeDistance;
    
    if (isLeftSwipe || isRightSwipe) {
      const currentIndex = views.findIndex(v => v.id === activeView);
      
      if (isLeftSwipe && currentIndex < views.length - 1) {
        setActiveView(views[currentIndex + 1].id);
      } else if (isRightSwipe && currentIndex > 0) {
        setActiveView(views[currentIndex - 1].id);
      }
    }
  };

  useEffect(() => {
    // Subscribe to store changes for real-time updates
    const unsubscribe = useStore.subscribe((state) => {
      // Handle errors
      if (state.errors) {
        if (state.errors.seasons) {
          setError(`Failed to load seasons: ${state.errors.seasons}`);
          setLoading(false);
          return;
        }
        if (state.errors.seasonTeams) {
          setError(`Failed to load teams: ${state.errors.seasonTeams}`);
          setLoading(false);
          return;
        }
        if (state.errors.players) {
          console.error('Players component: Players loading error:', state.errors.players);
        }
        if (state.errors.playerStats) {
          console.error('Players component: Player stats loading error:', state.errors.playerStats);
          setStatsLoading(false);
        }
      }

      if (state.seasons && state.seasons.length > 0) {
        setSeasons(state.seasons);
        setError(null);
        setLoading(false);
      }
    });

    // Initialize with current store state
    const currentState = useStore.getState();

    if (currentState.seasons && currentState.seasons.length > 0) {
      setSeasons(currentState.seasons);
      setLoading(false);
    } else {
      // Store empty — trigger fetch (subscriber above will handle the response)
      currentState.fetchSeasons();
    }

    return unsubscribe;
  }, []);

  // Subscribe to players data changes in store
  useEffect(() => {
    const unsubscribe = useStore.subscribe((state) => {
      if (activeSeason && state.players && Array.isArray(state.players)) {
        const playersForSeason = state.players.filter((player) => player.season_id === activeSeason);
        setSeasonPlayers(playersForSeason);
      }
    });
    return unsubscribe;
  }, [activeSeason]);

  // Subscribe to seasonTeams so component re-renders when lazy fetch completes
  useEffect(() => {
    const unsubscribe = useStore.subscribe((state) => {
      if (activeSeason && state.seasonTeams && state.seasonTeams[activeSeason]) {
        setSeasonTeams(state.seasonTeams[activeSeason] || []);
      }
    });
    return unsubscribe;
  }, [activeSeason]);

  // Subscribe to stats data changes in store
  useEffect(() => {
    const unsubscribe = useStore.subscribe((state) => {
      // Update stats data when it changes in store
      if (activeSeason && state.playerStats && state.playerStats[activeSeason]) {
        setStatsData(state.playerStats[activeSeason]);
        setStatsLoading(false);
      }

      // Handle stats loading state
      if (state.loading?.playerStats) {
        setStatsLoading(true);
      }
    });

    return unsubscribe;
  }, [activeSeason]);

  // Subscribe to fixtures data changes in store
  useEffect(() => {
    const unsubscribe = useStore.subscribe((state) => {
      // Update fixtures when they change in store
      if (activeSeason && state.fixtures && state.fixtures[activeSeason]) {
        setFixtures(state.fixtures[activeSeason]);
      }
    });

    return unsubscribe;
  }, [activeSeason]);

  // This will automatically switch users from Stats → Players whenever they click Season 1, 2, or 3 while Stats is selected.
   useEffect(() => {
  const currentSeason = seasons.find(
    (season) => season.id === activeSeason
  );

  const seasonNumber = parseInt(
    String(currentSeason?.name || '').replace('Season ', ''),
    10
  );

  if (activeView === 'stats' && seasonNumber < 4) {
    setActiveView('players');
  }
}   , [activeSeason, activeView, seasons]);


  // Load season-specific data when active season changes
  useEffect(() => {
    if (!activeSeason) return;
    const state = useStore.getState();

    // Fetch players if not already loaded (176kb — cached after first load)
    if (!state.players || state.players.length === 0) {
      state.fetchPlayers();
    } else {
      const playersForSeason = state.players.filter((p) => p.season_id === activeSeason);
      setSeasonPlayers(playersForSeason);
    }

    // Fetch seasonTeams if not already loaded (cached after first load)
    if (!state.seasonTeams || Object.keys(state.seasonTeams).length === 0) {
      state.fetchSeasonTeams();
    } else if (state.seasonTeams[activeSeason]) {
      setSeasonTeams(state.seasonTeams[activeSeason] || []);
    }

    // Get fixtures data from store
    if (state.fixtures && state.fixtures[activeSeason]) {
      setFixtures(state.fixtures[activeSeason]);
    } else {
      setFixtures([]);
      state.fetchFixtures(activeSeason);
    }
  }, [activeSeason]);

  // Set initial active season when seasons are loaded
  useEffect(() => {
    if (seasons.length > 0 && !activeSeason) {
      const state = useStore.getState();
      // Use current season from store or fallback to latest season
      const currentSeasonId = state.currentSeason?.id;

      if (currentSeasonId && seasons.find((s) => s.id === currentSeasonId)) {
        setActiveSeason(currentSeasonId);
      } else {
        // Sort seasons by start_date to get the latest season
        const sortedSeasons = [...seasons].sort((a, b) => new Date(b.start_date || 0) - new Date(a.start_date || 0));
        setActiveSeason(sortedSeasons[0].id);
      }
    }
  }, [seasons, activeSeason]);

  // Set initial active view
  useEffect(() => {
    if (!activeView) {
      setActiveView('players');
    }
  }, [activeView]);

  // Load stats data when stats view is active and season is selected
  useEffect(() => {
    if (activeView !== 'stats' || !activeSeason) {
      return;
    }
    const state = useStore.getState();

    // Check if stats data is already in store
    if (state.playerStats && state.playerStats[activeSeason]) {
      setStatsData(state.playerStats[activeSeason]);
      setStatsLoading(false);
    } else if (!state.loading?.playerStats) {
      state.fetchPlayerStats(activeSeason);
    }
  }, [activeView, activeSeason]);

  const views = [
    { id: 'players', name: 'Players' },
    { id: 'stats', name: 'Stats' },
  ];

  // Scroll function for horizontal scrolling
  const scroll = (ref, direction) => {
    if (ref.current) {
      const scrollAmount = 300;
      ref.current.scrollBy({
        left: direction === 'left' ? -scrollAmount : scrollAmount,
        behavior: 'smooth',
      });
    }
  };

  // Helper function to get player roles using centralized getRoleImage
  const getPlayerRoles = (roles) => {
    if (!roles || !Array.isArray(roles)) {
      return [getRoleImage('controller'), getRoleImage('duelist')]; // Default roles
    }

    return roles.map((role) => getRoleImage(role));
  };

  // Helper function to get team name for a player
  const getPlayerTeamName = useCallback(
    (playerId) => {
      if (!seasonTeams || seasonTeams.length === 0) {
        return 'No Team';
      }

      // Find the team that contains this player
      for (const team of seasonTeams) {
        if (team.players && team.players.some((player) => player.id === playerId)) {
          return team.name;
        }
      }

      return 'No Team';
    },
    [seasonTeams]
  );

  // Memoize allSeasonPlayers for filtering/sorting - updates when seasonPlayers or seasonTeams changes
  const allSeasonPlayers = useMemo(() => {
    // Use seasonPlayers data that gets updated when season changes
    if (seasonPlayers && seasonPlayers.length > 0) {
      return seasonPlayers.map((player) => {
        return {
          id: player.player_id, // Use player_id from the new structure
          name: player.game_name || player.name || player.valo_id?.split('#')[0],
          teamName: getPlayerTeamName(player.player_id), // Use player_id for team lookup
          avatar: player.photo && player.photo.length && player.verified ? player.photo : '/images/player_dummy.png',
          rank: getRankImage(player.peak_rank),
          roles: getPlayerRoles([player.prim_role, player.sec_role].filter(Boolean)),
          loading: false,
          verified: player.verified || false,
          prim_role: player.prim_role || '',
          sec_role: player.sec_role || '',
          peak_rank: player.peak_rank || '',
          // Additional data for potential future use
          valoId: player.valo_id,
          peakRank: player.peak_rank,
          currentRank: player.cur_rank,
          tracker: player.tracker,
        };
      });
    }

    return [];
  }, [seasonPlayers, getPlayerTeamName]);

  const filteredAndSortedPlayers = useMemo(() => {
    return allSeasonPlayers
      .filter((player) => {
        const matchesSearch = player.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
          player.teamName.toLowerCase().includes(searchQuery.toLowerCase());
        const matchesTeam     = selectedTeam === 'all' || player.teamName === selectedTeam;
        const matchesRole     = selectedRole === 'all' || (player.prim_role || '').toLowerCase() === selectedRole || (player.sec_role || '').toLowerCase() === selectedRole;
        const matchesRank     = selectedRank === 'all' || (player.peak_rank || '').toLowerCase().includes(selectedRank.toLowerCase());
        const matchesVerified = selectedVerified === 'all' || (selectedVerified === 'verified' ? player.verified : !player.verified);
        return matchesSearch && matchesTeam && matchesRole && matchesRank && matchesVerified;
      })
      .sort((a, b) => {
        switch (sortOption) {
          case 'name': return a.name.localeCompare(b.name);
          case 'rank': return getRankValue(b.rank) - getRankValue(a.rank);
          default: return 0;
        }
      });
  }, [allSeasonPlayers, searchQuery, sortOption, selectedTeam, selectedRole, selectedRank, selectedVerified]);

  // Memoize filtered stats data - updates when statsData, search, or filters change
  const filteredStatsData = useMemo(() => {
    if (!Array.isArray(statsData)) return [];

    // Apply all filters
    const filteredData = statsData.filter((player) => {
      // Search filter
      const playerName = player.game_name || player.valo_id?.split('#')[0] || '';
      const teamName = player.team_name || '';
      const searchLower = searchQuery.toLowerCase();
      const matchesSearch = playerName.toLowerCase().includes(searchLower) || teamName.toLowerCase().includes(searchLower);
      
      // Team filter
      const matchesTeam = selectedTeam === 'all' || teamName === selectedTeam;
      
      return matchesSearch && matchesTeam;
    });

    // Sort by kills and assign positions
    const sortedData = filteredData.sort((a, b) => (b.total_kills || 0) - (a.total_kills || 0));
    
    // Assign positions (rank) based on kills
    return sortedData.map((player, index) => ({
      ...player,
      position: index + 1 // 1-based position
    }));
  }, [statsData, searchQuery, selectedTeam]);

  // Show loading state
  if (loading) {
    return (
      <div className="players-container">
        <div className="season-status-message">
          <h2>Loading...</h2>
          <p>Loading players data...</p>
        </div>
      </div>
    );
  }

  // Show error state
  if (error) {
    return (
      <div className="players-container">
        <div className="season-status-message">
          <h2>Error</h2>
          <p>{error}</p>
        </div>
      </div>
    );
  }

  // Show no seasons state
  if (seasons.length === 0) {
    return (
      <div className="players-container">
        <div className="season-status-message">
          <h2>No Seasons Available</h2>
          <p>There are no seasons configured yet. Please check back later.</p>
        </div>
      </div>
    );
  }

  return (
    <div 
      className="players-container"
      onTouchStart={isMobileView ? onTouchStart : undefined}
      onTouchMove={isMobileView ? onTouchMove : undefined}
      onTouchEnd={isMobileView ? onTouchEnd : undefined}
    >
      <div className="season-tabs-wrapper">
        <button className="scroll-arrow left" onClick={() => scroll(seasonTabsRef, 'left')} aria-label="Scroll left">
          <svg
            xmlns="http://www.w3.org/2000/svg"
            width="24"
            height="24"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth="2"
            strokeLinecap="round"
            strokeLinejoin="round"
          >
            <polyline points="15 18 9 12 15 6"></polyline>
          </svg>
        </button>
        <div className="season-tabs" ref={seasonTabsRef}>
          {seasons.map((season) => (
            <div
              key={season.id}
              className={`season-tab ${activeSeason === season.id ? 'active' : ''}`}
              onClick={() => {
                setActiveSeason(season.id);
                // Update the store's current season
                useStore.getState().setCurrentSeason(season);
              }}
            >
              <span className="season-name">{season.name}</span>
            </div>
          ))}
        </div>
        <button className="scroll-arrow right" onClick={() => scroll(seasonTabsRef, 'right')} aria-label="Scroll right">
          <svg
            xmlns="http://www.w3.org/2000/svg"
            width="24"
            height="24"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth="2"
            strokeLinecap="round"
            strokeLinejoin="round"
          >
            <polyline points="9 18 15 12 9 6"></polyline>
          </svg>
        </button>
      </div>

      <div className="view-tabs">
        {views.filter((view) => {
          if (view.id !== 'stats') return true;

          const currentSeason = seasons.find((season) => season.id === activeSeason);
          const seasonNumber = parseInt(
            String(currentSeason?.name || '').replace('Season ', ''),
            10
          );

          return seasonNumber >= 4;
        }).map((view) => (
          <div
            key={view.id}
            className={`view-tab ${activeView === view.id ? 'active' : ''}`}
            onClick={() => setActiveView(view.id)}
          >
            <span className="tab-name">{view.name}</span>
          </div>
        ))}
        {isMobileView && (
          <div className="swipe-hint">
            <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <polyline points="15 18 9 12 15 6"></polyline>
            </svg>
            <span>Swipe</span>
            <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <polyline points="9 18 15 12 9 6"></polyline>
            </svg>
          </div>
        )}
      </div>

      {activeView === 'players' && (
        <div className="player-view-container">
          <SearchSortControls
            searchQuery={searchQuery}
            setSearchQuery={setSearchQuery}
            sortOption={sortOption}
            setSortOption={setSortOption}
            activeView={activeView}
          />

          {/* Interactive Filters for Players */}
          <div className="filters-section">
            <button 
              className="filters-toggle"
              onClick={() => setShowFilters(!showFilters)}
            >
              <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <polygon points="22 3 2 3 10 12.46 10 19 14 21 14 12.46 22 3"></polygon>
              </svg>
              <span>Filters</span>
              {selectedTeam !== 'all' && (
                <span className="filter-badge">1</span>
              )}
              <svg 
                xmlns="http://www.w3.org/2000/svg" 
                width="16" 
                height="16" 
                viewBox="0 0 24 24" 
                fill="none" 
                stroke="currentColor" 
                strokeWidth="2"
                style={{ transform: showFilters ? 'rotate(180deg)' : 'rotate(0deg)', transition: 'transform 0.3s' }}
              >
                <polyline points="6 9 12 15 18 9"></polyline>
              </svg>
            </button>
            
            {showFilters && (
              <div className="filters-container">
                <div className="filter-group">
                  <label className="filter-label">
                    <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                      <path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2"></path>
                      <circle cx="9" cy="7" r="4"></circle>
                      <path d="M23 21v-2a4 4 0 0 0-3-3.87"></path>
                      <path d="M16 3.13a4 4 0 0 1 0 7.75"></path>
                    </svg>
                    Team
                  </label>
                  <select className="filter-select" value={selectedTeam} onChange={(e) => setSelectedTeam(e.target.value)}>
                    <option value="all">All Teams</option>
                    {seasonTeams && seasonTeams.length > 0 && (
                      seasonTeams.map((team) => (
                        <option key={team.id} value={team.name}>{team.name}</option>
                      ))
                    )}
                  </select>
                </div>

                <div className="filter-group">
                  <label className="filter-label">Role</label>
                  <select className="filter-select" value={selectedRole} onChange={(e) => setSelectedRole(e.target.value)}>
                    <option value="all">All Roles</option>
                    <option value="duelist">Duelist</option>
                    <option value="initiator">Initiator</option>
                    <option value="controller">Controller</option>
                    <option value="sentinel">Sentinel</option>
                    <option value="flex">Flex</option>
                  </select>
                </div>

                <div className="filter-group">
                  <label className="filter-label">Peak Rank</label>
                  <select className="filter-select" value={selectedRank} onChange={(e) => setSelectedRank(e.target.value)}>
                    <option value="all">All Ranks</option>
                    <option value="Radiant">Radiant</option>
                    <optgroup label="Immortal">
                      <option value="Immortal 3">Immortal 3</option>
                      <option value="Immortal 2">Immortal 2</option>
                      <option value="Immortal 1">Immortal 1</option>
                    </optgroup>
                    <optgroup label="Ascendant">
                      <option value="Ascendant 3">Ascendant 3</option>
                      <option value="Ascendant 2">Ascendant 2</option>
                      <option value="Ascendant 1">Ascendant 1</option>
                    </optgroup>
                    <optgroup label="Diamond">
                      <option value="Diamond 3">Diamond 3</option>
                      <option value="Diamond 2">Diamond 2</option>
                      <option value="Diamond 1">Diamond 1</option>
                    </optgroup>
                    <optgroup label="Platinum">
                      <option value="Platinum 3">Platinum 3</option>
                      <option value="Platinum 2">Platinum 2</option>
                      <option value="Platinum 1">Platinum 1</option>
                    </optgroup>
                  </select>
                </div>

                <div className="filter-group">
                  <label className="filter-label">Status</label>
                  <select className="filter-select" value={selectedVerified} onChange={(e) => setSelectedVerified(e.target.value)}>
                    <option value="all">All Players</option>
                    <option value="verified">Verified</option>
                    <option value="unverified">Unverified</option>
                  </select>
                </div>

                <button
                  className="clear-filters-btn"
                  onClick={() => { setSelectedTeam('all'); setSelectedRole('all'); setSelectedRank('all'); setSelectedVerified('all'); }}
                  disabled={selectedTeam === 'all' && selectedRole === 'all' && selectedRank === 'all' && selectedVerified === 'all'}
                >
                  <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                    <polyline points="1 4 1 10 7 10"></polyline>
                    <path d="M3.51 15a9 9 0 1 0 2.13-9.36L1 10"></path>
                  </svg>
                  Clear Filters
                </button>
              </div>
            )}
          </div>

          {/* Player count display */}
          <div className="player-count-info">
            <span className="player-count">
              {loading ? 'Loading...' : `${filteredAndSortedPlayers.length}`}
              {(searchQuery || selectedTeam !== 'all' || selectedRole !== 'all' || selectedRank !== 'all' || selectedVerified !== 'all') && ` from ${allSeasonPlayers.length}`}
              {` players`}
            </span>
            {selectedTeam !== 'all' && (
              <div className="active-filters-summary">
                <span className="filter-chip">{selectedTeam}</span>
              </div>
            )}
          </div>

          <div className="player-cards-container">
            {/* Display all players in a responsive grid */}
            {filteredAndSortedPlayers.length > 0 ? (
              // Create rows of 3 players each
              Array.from({ length: Math.ceil(filteredAndSortedPlayers.length / 3) }, (_, rowIndex) => (
                <div key={`row-${rowIndex}`} className="player-cards-row">
                  {filteredAndSortedPlayers.slice(rowIndex * 3, (rowIndex + 1) * 3).map((player, playerIndex) => (
                    <div key={`${player.id}-${player.teamName}-${rowIndex}-${playerIndex}`} className="player-card">
                      <div className="player-info">
                        <div
                          className={`player-name ${player.tracker ? 'clickable' : ''}`}
                          onClick={() => {
                            if (player.tracker) {
                              window.open(player.tracker, '_blank', 'noopener,noreferrer');
                            }
                          }}
                          style={{ cursor: player.tracker ? 'pointer' : 'default' }}
                        >
                          {player.name}
                        </div>
                        <div className="player-team-name" style={{ marginBottom: player.verified ? 0 : undefined }}>{player.teamName}</div>
                        {player.verified && (
                          <div className="player-verified-text">VERIFIED</div>
                        )}
                        <div className="player-details">
                          <div className="player-rank-label">Rank</div>
                          <div className="player-rank">
                            <img src={player.rank} alt="Player Rank" />
                          </div>
                          <div className="player-roles-label">Roles</div>
                          <div className="player-roles">
                            {player.roles.map((role, index) => (
                              <img key={index} src={role} alt={`Role ${index + 1}`} />
                            ))}
                          </div>
                        </div>
                      </div>
                      <div className="player-avatar">
                        <img src={player.avatar} alt={player.name} />
                      </div>
                    </div>
                  ))}
                </div>
              ))
            ) : (
              <div className="no-players-message">
                <h3>Our roster is warming up… stay tuned</h3>
              </div>
            )}
          </div>
        </div>
      )}

      {activeView === 'stats' && (
        <div className="stats-container">
          <SearchSortControls
            searchQuery={searchQuery}
            setSearchQuery={setSearchQuery}
            sortOption={sortOption}
            setSortOption={setSortOption}
            activeView={activeView}
          />

          {/* Interactive Filters */}
          <div className="filters-section">
            <button 
              className="filters-toggle"
              onClick={() => setShowFilters(!showFilters)}
            >
              <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <polygon points="22 3 2 3 10 12.46 10 19 14 21 14 12.46 22 3"></polygon>
              </svg>
              <span>Filters</span>
              {selectedTeam !== 'all' && (
                <span className="filter-badge">1</span>
              )}
              <svg 
                xmlns="http://www.w3.org/2000/svg" 
                width="16" 
                height="16" 
                viewBox="0 0 24 24" 
                fill="none" 
                stroke="currentColor" 
                strokeWidth="2"
                style={{ transform: showFilters ? 'rotate(180deg)' : 'rotate(0deg)', transition: 'transform 0.3s' }}
              >
                <polyline points="6 9 12 15 18 9"></polyline>
              </svg>
            </button>
            
            {showFilters && (
              <div className="filters-container">
                <div className="filter-group">
                  <label className="filter-label">
                    <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                      <path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2"></path>
                      <circle cx="9" cy="7" r="4"></circle>
                      <path d="M23 21v-2a4 4 0 0 0-3-3.87"></path>
                      <path d="M16 3.13a4 4 0 0 1 0 7.75"></path>
                    </svg>
                    Team
                  </label>
                  <select 
                    className="filter-select"
                    value={selectedTeam}
                    onChange={(e) => setSelectedTeam(e.target.value)}
                  >
                    <option value="all">All Teams</option>
                    {seasonTeams && seasonTeams.length > 0 && (
                      seasonTeams.map((team) => (
                        <option key={team.id} value={team.name}>{team.name}</option>
                      ))
                    )}
                  </select>
                </div>

                <button 
                  className="clear-filters-btn"
                  onClick={() => {
                    setSelectedTeam('all');
                  }}
                  disabled={selectedTeam === 'all'}
                >
                  <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                    <polyline points="1 4 1 10 7 10"></polyline>
                    <path d="M3.51 15a9 9 0 1 0 2.13-9.36L1 10"></path>
                  </svg>
                  Clear Filters
                </button>
              </div>
            )}
          </div>

          {/* Results count and active filters */}
          {!statsLoading && filteredStatsData && filteredStatsData.length > 0 && (
            <div className="stats-count-info">
              <span className="stats-count">
                {`${filteredStatsData.length}`}
                {(searchQuery || selectedTeam !== 'all') && 
                  ` from ${statsData.length}`}
                {` players`}
              </span>
              {selectedTeam !== 'all' && (
                <div className="active-filters-summary">
                  <span className="filter-chip">{selectedTeam}</span>
                </div>
              )}
            </div>
          )}

          {/* View Mode Toggle for Mobile */}
          {isMobileView && (
            <div className="view-mode-toggle">
              <button
                className={`toggle-btn ${viewMode === 'compact' ? 'active' : ''}`}
                onClick={() => setViewMode('compact')}
              >
                <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <rect x="3" y="3" width="7" height="7"></rect>
                  <rect x="14" y="3" width="7" height="7"></rect>
                  <rect x="3" y="14" width="7" height="7"></rect>
                  <rect x="14" y="14" width="7" height="7"></rect>
                </svg>
                <span>Cards</span>
              </button>
              <button
                className={`toggle-btn ${viewMode === 'detailed' ? 'active' : ''}`}
                onClick={() => setViewMode('detailed')}
              >
                <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <line x1="8" y1="6" x2="21" y2="6"></line>
                  <line x1="8" y1="12" x2="21" y2="12"></line>
                  <line x1="8" y1="18" x2="21" y2="18"></line>
                  <line x1="3" y1="6" x2="3.01" y2="6"></line>
                  <line x1="3" y1="12" x2="3.01" y2="12"></line>
                  <line x1="3" y1="18" x2="3.01" y2="18"></line>
                </svg>
                <span>Table</span>
              </button>
            </div>
          )}

          {/* Stats loading indicator */}
          {statsLoading && (
            <div className="stats-loading">
              <h3>Loading statistics...</h3>
            </div>
          )}

          {/* Stats Summary Cards */}
          {!statsLoading && filteredStatsData && filteredStatsData.length > 0 && (
            <div className="stats-summary-cards">
              <div className="summary-card">
                <div className="summary-icon">
                  <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                    <path d="M16 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2"></path>
                    <circle cx="8.5" cy="7" r="4"></circle>
                    <polyline points="17 11 19 13 23 9"></polyline>
                  </svg>
                </div>
                <div className="summary-content">
                  <div className="summary-value">{filteredStatsData.length}</div>
                  <div className="summary-label">Active Players</div>
                </div>
              </div>
              <div className="summary-card">
                <div className="summary-icon">
                  <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                    <path d="M22 12h-4l-3 9L9 3l-3 9H2"></path>
                  </svg>
                </div>
                <div className="summary-content">
                  <div className="summary-value">
                    {filteredStatsData.reduce((sum, p) => sum + (p.total_kills || 0), 0).toLocaleString()}
                  </div>
                  <div className="summary-label">Total Kills</div>
                </div>
              </div>
              <div className="summary-card">
                <div className="summary-icon">
                  <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                    <rect x="3" y="4" width="18" height="18" rx="2" ry="2"></rect>
                    <line x1="16" y1="2" x2="16" y2="6"></line>
                    <line x1="8" y1="2" x2="8" y2="6"></line>
                    <line x1="3" y1="10" x2="21" y2="10"></line>
                  </svg>
                </div>
                <div className="summary-content">
                  <div className="summary-value">
                    {(() => {
                      // Count completed matches from fixtures
                      if (fixtures && fixtures.grouped_fixtures) {
                        // Filter function to check if match involves selected team
                        const isTeamMatch = (match) => {
                          if (selectedTeam === 'all') return true;
                          return match.attacker_team?.name === selectedTeam || 
                                 match.defender_team?.name === selectedTeam;
                        };
                        
                        const completedCount = fixtures.grouped_fixtures.reduce((total, group) => {
                          return total + (group.matches || []).filter(match => 
                            match.status === 'completed' && isTeamMatch(match)
                          ).length;
                        }, 0);
                        // Add ungrouped matches if any
                        const ungroupedCompleted = (fixtures.ungrouped_matches || []).filter(match => 
                          match.status === 'completed' && isTeamMatch(match)
                        ).length;
                        return completedCount + ungroupedCompleted;
                      }
                      // Fallback to player stats if fixtures not loaded
                      return filteredStatsData.length > 0 ? Math.max(...filteredStatsData.map(p => p.matches_played || 0)) : 0;
                    })()}
                  </div>
                  <div className="summary-label">Total Matches</div>
                </div>
              </div>
              <div className="summary-card">
                <div className="summary-icon">
                  <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                    <line x1="12" y1="1" x2="12" y2="23"></line>
                    <path d="M17 5H9.5a3.5 3.5 0 0 0 0 7h5a3.5 3.5 0 0 1 0 7H6"></path>
                  </svg>
                </div>
                <div className="summary-content">
                  <div className="summary-value">
                    {(filteredStatsData.reduce((sum, p) => sum + ((p.total_kills || 0) / Math.max(p.total_deaths || 1, 1)), 0) / filteredStatsData.length).toFixed(2)}
                  </div>
                  <div className="summary-label">Avg K/D</div>
                </div>
              </div>
            </div>
          )}

          {/* Top 3 Podium Display */}
          {!statsLoading && filteredStatsData && filteredStatsData.length >= 3 && (
            <div className="podium-container">
              <div className="podium-title">
                <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <circle cx="12" cy="8" r="7"></circle>
                  <polyline points="8.21 13.89 7 23 12 20 17 23 15.79 13.88"></polyline>
                </svg>
                <h2>Top Performers</h2>
              </div>
              <div className="podium-display">
                {/* 2nd Place */}
                {filteredStatsData[1] && (
                  <div className="podium-player podium-second">
                    <div className="podium-rank">🥈</div>
                    <div className="podium-avatar">
                      <img
                        src={getRankImage(filteredStatsData[1].peak_rank)}
                        alt="Rank"
                      />
                    </div>
                    <div className="podium-info">
                      <div className="podium-name">
                        {filteredStatsData[1].game_name || filteredStatsData[1].valo_id?.split('#')[0] || 'Unknown'}
                      </div>
                      <div className="podium-team">{filteredStatsData[1].team_name}</div>
                      <div className="podium-stats">
                        <div className="podium-stat-item">
                          <span className="stat-label">Kills</span>
                          <span className="stat-value">{filteredStatsData[1].total_kills}</span>
                        </div>
                        <div className="podium-stat-item">
                          <span className="stat-label">K/D</span>
                          <span className="stat-value">
                            {(filteredStatsData[1].total_kills / Math.max(filteredStatsData[1].total_deaths, 1)).toFixed(2)}
                          </span>
                        </div>
                      </div>
                    </div>
                    <div className="podium-base podium-base-2">2</div>
                  </div>
                )}

                {/* 1st Place */}
                {filteredStatsData[0] && (
                  <div className="podium-player podium-first">
                    <div className="podium-crown">👑</div>
                    <div className="podium-rank">🥇</div>
                    <div className="podium-avatar">
                      <img
                        src={getRankImage(filteredStatsData[0].peak_rank)}
                        alt="Rank"
                      />
                    </div>
                    <div className="podium-info">
                      <div className="podium-name">
                        {filteredStatsData[0].game_name || filteredStatsData[0].valo_id?.split('#')[0] || 'Unknown'}
                      </div>
                      <div className="podium-team">{filteredStatsData[0].team_name}</div>
                      <div className="podium-stats">
                        <div className="podium-stat-item">
                          <span className="stat-label">Kills</span>
                          <span className="stat-value">{filteredStatsData[0].total_kills}</span>
                        </div>
                        <div className="podium-stat-item">
                          <span className="stat-label">K/D</span>
                          <span className="stat-value">
                            {(filteredStatsData[0].total_kills / Math.max(filteredStatsData[0].total_deaths, 1)).toFixed(2)}
                          </span>
                        </div>
                      </div>
                    </div>
                    <div className="podium-base podium-base-1">1</div>
                  </div>
                )}

                {/* 3rd Place */}
                {filteredStatsData[2] && (
                  <div className="podium-player podium-third">
                    <div className="podium-rank">🥉</div>
                    <div className="podium-avatar">
                      <img
                        src={getRankImage(filteredStatsData[2].peak_rank)}
                        alt="Rank"
                      />
                    </div>
                    <div className="podium-info">
                      <div className="podium-name">
                        {filteredStatsData[2].game_name || filteredStatsData[2].valo_id?.split('#')[0] || 'Unknown'}
                      </div>
                      <div className="podium-team">{filteredStatsData[2].team_name}</div>
                      <div className="podium-stats">
                        <div className="podium-stat-item">
                          <span className="stat-label">Kills</span>
                          <span className="stat-value">{filteredStatsData[2].total_kills}</span>
                        </div>
                        <div className="podium-stat-item">
                          <span className="stat-label">K/D</span>
                          <span className="stat-value">
                            {(filteredStatsData[2].total_kills / Math.max(filteredStatsData[2].total_deaths, 1)).toFixed(2)}
                          </span>
                        </div>
                      </div>
                    </div>
                    <div className="podium-base podium-base-3">3</div>
                  </div>
                )}
              </div>
            </div>
          )}

          {/* Charts and Graphs Section */}
          {!statsLoading && filteredStatsData && filteredStatsData.length > 0 && (
            <div className="charts-section">
              <div className="charts-toggle-header">
                <button 
                  className="charts-toggle-btn"
                  onClick={() => setShowCharts(!showCharts)}
                >
                  <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                    <line x1="12" y1="20" x2="12" y2="10"></line>
                    <line x1="18" y1="20" x2="18" y2="4"></line>
                    <line x1="6" y1="20" x2="6" y2="16"></line>
                  </svg>
                  <span>Performance Analytics</span>
                  <svg 
                    className={`toggle-arrow ${showCharts ? 'rotated' : ''}`}
                    xmlns="http://www.w3.org/2000/svg" 
                    width="20" 
                    height="20" 
                    viewBox="0 0 24 24" 
                    fill="none" 
                    stroke="currentColor" 
                    strokeWidth="2"
                  >
                    <polyline points="6 9 12 15 18 9"></polyline>
                  </svg>
                </button>
              </div>

              {showCharts && (
                <>
                  <div className="charts-header">
                    <div className="charts-header-left">
                      <h2>Detailed Statistics</h2>
                    </div>
                    
                    {/* Chart Filters */}
                    <div className="chart-filters">
                      <div className="chart-filter-group">
                        <label htmlFor="chart-metric">Metric:</label>
                        <select 
                          id="chart-metric"
                          className="chart-filter-select" 
                          value={chartMetric} 
                          onChange={(e) => setChartMetric(e.target.value)}
                        >
                          <option value="kills">Kills</option>
                          <option value="kd">K/D Ratio</option>
                          <option value="assists">Assists</option>
                        </select>
                      </div>
                      
                      <div className="chart-filter-group">
                        <label htmlFor="chart-player-count">Show Top:</label>
                        <select 
                          id="chart-player-count"
                          className="chart-filter-select" 
                          value={chartPlayerCount} 
                          onChange={(e) => setChartPlayerCount(Number(e.target.value))}
                        >
                          <option value={5}>5 Players</option>
                          <option value={10}>10 Players</option>
                          <option value={15}>15 Players</option>
                          <option value={20}>20 Players</option>
                        </select>
                      </div>
                    </div>
                  </div>

                  <div className="charts-grid">
                {/* Top Players Bar Chart */}
                <div className="chart-card">
                  <h3 className="chart-title">
                    Top {chartPlayerCount} Players by {chartMetric === 'kills' ? 'Kills' : chartMetric === 'kd' ? 'K/D Ratio' : 'Assists'}
                  </h3>
                  <ResponsiveContainer width="100%" height={400}>
                    <BarChart
                      data={(() => {
                        const sortedData = [...filteredStatsData]
                          .sort((a, b) => {
                            if (chartMetric === 'kd') {
                              const kdA = a.total_kills / Math.max(a.total_deaths, 1);
                              const kdB = b.total_kills / Math.max(b.total_deaths, 1);
                              return kdB - kdA;
                            } else if (chartMetric === 'assists') {
                              return (b.total_assists || 0) - (a.total_assists || 0);
                            }
                            return (b.total_kills || 0) - (a.total_kills || 0);
                          })
                          .slice(0, chartPlayerCount);
                        
                        return sortedData.map(player => ({
                          name: player.game_name || player.valo_id?.split('#')[0] || 'Unknown',
                          kills: player.total_kills || 0,
                          deaths: player.total_deaths || 0,
                          assists: player.total_assists || 0,
                          kd: (player.total_kills / Math.max(player.total_deaths, 1)).toFixed(2),
                        }));
                      })()}
                      margin={{ top: 20, right: 30, left: 20, bottom: 80 }}
                      barGap={8}
                      barCategoryGap="20%"
                    >
                      <CartesianGrid strokeDasharray="3 3" stroke="rgba(255,255,255,0.1)" />
                      <XAxis 
                        dataKey="name" 
                        angle={-45} 
                        textAnchor="end" 
                        height={120}
                        interval={0}
                        tick={{ fill: '#fff', fontSize: 11 }}
                      />
                      <YAxis tick={{ fill: '#fff' }} />
                      <Tooltip 
                        contentStyle={{ 
                          backgroundColor: 'rgba(20, 20, 40, 0.95)', 
                          border: '1px solid rgba(255, 70, 85, 0.3)',
                          borderRadius: '8px',
                          color: '#fff'
                        }} 
                      />
                      <Legend wrapperStyle={{ color: '#fff', paddingTop: '20px' }} />
                      <Bar dataKey="kills" fill="#ff4655" name="Kills" radius={[8, 8, 0, 0]} />
                      <Bar dataKey="deaths" fill="#4a90e2" name="Deaths" radius={[8, 8, 0, 0]} />
                      <Bar dataKey="assists" fill="#50c878" name="Assists" radius={[8, 8, 0, 0]} />
                    </BarChart>
                  </ResponsiveContainer>
                </div>

                {/* Team Performance Comparison */}
                {selectedTeam === 'all' && (
                  <div className="chart-card chart-card-wide">
                    <div className="chart-header-with-filter">
                      <h3 className="chart-title">Team Performance Comparison</h3>
                      <div className="chart-metric-toggle">
                        <button 
                          className={`metric-toggle-btn ${teamMetric === 'all' ? 'active' : ''}`}
                          onClick={() => setTeamMetric('all')}
                        >
                          All Stats
                        </button>
                        <button 
                          className={`metric-toggle-btn ${teamMetric === 'kills' ? 'active' : ''}`}
                          onClick={() => setTeamMetric('kills')}
                        >
                          Kills Only
                        </button>
                        <button 
                          className={`metric-toggle-btn ${teamMetric === 'deaths' ? 'active' : ''}`}
                          onClick={() => setTeamMetric('deaths')}
                        >
                          Deaths Only
                        </button>
                        <button 
                          className={`metric-toggle-btn ${teamMetric === 'assists' ? 'active' : ''}`}
                          onClick={() => setTeamMetric('assists')}
                        >
                          Assists Only
                        </button>
                      </div>
                    </div>
                    <ResponsiveContainer width="100%" height={400}>
                      <BarChart
                        data={(() => {
                          const teamStats = {};
                          filteredStatsData.forEach(player => {
                            const team = player.team_name;
                            if (!teamStats[team]) {
                              teamStats[team] = {
                                name: team,
                                totalKills: 0,
                                totalDeaths: 0,
                                totalAssists: 0,
                                playerCount: 0
                              };
                            }
                            teamStats[team].totalKills += player.total_kills || 0;
                            teamStats[team].totalDeaths += player.total_deaths || 0;
                            teamStats[team].totalAssists += player.total_assists || 0;
                            teamStats[team].playerCount += 1;
                          });
                          // Sort teams by the selected metric in descending order
                          const sortKey = teamMetric === 'deaths' ? 'avgDeaths' : 
                                         teamMetric === 'assists' ? 'avgAssists' : 'avgKills';
                          return Object.values(teamStats)
                            .map(team => ({
                              name: team.name,
                              avgKills: parseFloat((team.totalKills / team.playerCount).toFixed(1)),
                              avgDeaths: parseFloat((team.totalDeaths / team.playerCount).toFixed(1)),
                              avgAssists: parseFloat((team.totalAssists / team.playerCount).toFixed(1)),
                            }))
                            .sort((a, b) => b[sortKey] - a[sortKey]);
                        })()}
                        margin={{ top: 20, right: 30, left: 20, bottom: 80 }}
                        barGap={8}
                        barCategoryGap="20%"
                      >
                        <CartesianGrid strokeDasharray="3 3" stroke="rgba(255,255,255,0.1)" />
                        <XAxis 
                          dataKey="name" 
                          angle={-45} 
                          textAnchor="end" 
                          height={120}
                          interval={0}
                          tick={{ fill: '#fff', fontSize: 12 }}
                        />
                        <YAxis tick={{ fill: '#fff' }} label={{ value: 'Average Stats', angle: -90, position: 'insideLeft', fill: '#fff' }} />
                        <Tooltip 
                          contentStyle={{ 
                            backgroundColor: 'rgba(20, 20, 40, 0.95)', 
                            border: '1px solid rgba(255, 70, 85, 0.3)',
                            borderRadius: '8px',
                            color: '#fff'
                          }} 
                        />
                        <Legend wrapperStyle={{ color: '#fff', paddingTop: '20px' }} />
                        {(teamMetric === 'all' || teamMetric === 'kills') && (
                          <Bar dataKey="avgKills" fill="#ff4655" name="Avg Kills" radius={[8, 8, 0, 0]} />
                        )}
                        {(teamMetric === 'all' || teamMetric === 'deaths') && (
                          <Bar dataKey="avgDeaths" fill="#4a90e2" name="Avg Deaths" radius={[8, 8, 0, 0]} />
                        )}
                        {(teamMetric === 'all' || teamMetric === 'assists') && (
                          <Bar dataKey="avgAssists" fill="#50c878" name="Avg Assists" radius={[8, 8, 0, 0]} />
                        )}
                      </BarChart>
                    </ResponsiveContainer>
                  </div>
                )}

                {/* Performance Trend */}
                <div className="chart-card">
                  <div className="chart-header-with-filter">
                    <h3 className="chart-title">Player Performance Levels</h3>
                    <div className="chart-metric-toggle">
                      <button 
                        className={`metric-toggle-btn ${performanceMetric === 'all' ? 'active' : ''}`}
                        onClick={() => setPerformanceMetric('all')}
                      >
                        All Stats
                      </button>
                      <button 
                        className={`metric-toggle-btn ${performanceMetric === 'kills' ? 'active' : ''}`}
                        onClick={() => setPerformanceMetric('kills')}
                      >
                        Kills Only
                      </button>
                      <button 
                        className={`metric-toggle-btn ${performanceMetric === 'deaths' ? 'active' : ''}`}
                        onClick={() => setPerformanceMetric('deaths')}
                      >
                        Deaths Only
                      </button>
                      <button 
                        className={`metric-toggle-btn ${performanceMetric === 'assists' ? 'active' : ''}`}
                        onClick={() => setPerformanceMetric('assists')}
                      >
                        Assists Only
                      </button>
                    </div>
                  </div>
                  <ResponsiveContainer width="100%" height={400}>
                    <LineChart
                      data={filteredStatsData.slice(0, chartPlayerCount).map((player, idx) => ({
                        position: idx + 1,
                        name: player.game_name || player.valo_id?.split('#')[0] || 'Unknown',
                        kills: player.total_kills || 0,
                        deaths: player.total_deaths || 0,
                        assists: player.total_assists || 0,
                      }))}
                      margin={{ top: 20, right: 30, left: 20, bottom: 80 }}
                    >
                      <CartesianGrid strokeDasharray="3 3" stroke="rgba(255,255,255,0.1)" />
                      <XAxis 
                        dataKey="name"
                        angle={-45}
                        textAnchor="end"
                        height={120}
                        interval={0}
                        tick={{ fill: '#fff', fontSize: 11 }}
                      />
                      <YAxis tick={{ fill: '#fff' }} />
                      <Tooltip 
                        contentStyle={{ 
                          backgroundColor: 'rgba(20, 20, 40, 0.95)', 
                          border: '1px solid rgba(255, 70, 85, 0.3)',
                          borderRadius: '8px',
                          color: '#fff'
                        }}
                      />
                      <Legend wrapperStyle={{ color: '#fff', paddingTop: '20px' }} />
                      {(performanceMetric === 'all' || performanceMetric === 'kills') && (
                        <Line type="monotone" dataKey="kills" stroke="#ff4655" strokeWidth={3} name="Total Kills" dot={{ r: 4 }} activeDot={{ r: 6 }} />
                      )}
                      {(performanceMetric === 'all' || performanceMetric === 'deaths') && (
                        <Line type="monotone" dataKey="deaths" stroke="#4a90e2" strokeWidth={3} name="Total Deaths" dot={{ r: 4 }} activeDot={{ r: 6 }} />
                      )}
                      {(performanceMetric === 'all' || performanceMetric === 'assists') && (
                        <Line type="monotone" dataKey="assists" stroke="#50c878" strokeWidth={3} name="Total Assists" dot={{ r: 4 }} activeDot={{ r: 6 }} />
                      )}
                    </LineChart>
                  </ResponsiveContainer>
                </div>
              </div>
                </>
              )}
            </div>
          )}

          <div className="stats-table">
            {!statsLoading && filteredStatsData && filteredStatsData.length > 0 ? (
              <div className="stats-content">
                {/* Mobile Card View */}
                {isMobileView && viewMode === 'compact' ? (
                  <div className="stats-cards-container">
                    {filteredStatsData
                      .sort((a, b) => {
                        const direction = statsSortDirection === 'asc' ? 1 : -1;
                        switch (statsSortField) {
                          case 'kills':
                            return direction * ((a.total_kills || 0) - (b.total_kills || 0));
                          case 'kd':
                            const kdA = (a.total_kills || 0) / Math.max(a.total_deaths || 1, 1);
                            const kdB = (b.total_kills || 0) / Math.max(b.total_deaths || 1, 1);
                            return direction * (kdA - kdB);
                          default:
                            return 0;
                        }
                      })
                      .map((player) => {
                        const playerData = seasonPlayers.find((p) => p.player_id === player.player_id);
                        const trackerUrl = playerData?.tracker;
                        const kd = (player.total_kills / Math.max(player.total_deaths, 1)).toFixed(2);
                        const isPodium = player.position <= 3;

                        return (
                          <div 
                            key={player.player_id} 
                            className={`stats-card ${isPodium ? `podium-${player.position}` : ''}`}
                            onClick={() => {
                              if (trackerUrl) {
                                window.open(trackerUrl, '_blank', 'noopener,noreferrer');
                              }
                            }}
                            style={{
                              cursor: trackerUrl ? 'pointer' : 'default'
                            }}
                          >
                            <div className="stats-card-header">
                              <div className="stats-card-position">
                                {isPodium && (
                                  <span className="podium-medal">
                                    {player.position === 1 ? '🥇' : player.position === 2 ? '🥈' : '🥉'}
                                  </span>
                                )}
                                <span className="position-number">#{player.position}</span>
                              </div>
                              <div className="stats-card-rank">
                                <img
                                  src={getRankImage(player.peak_rank)}
                                  alt={player.peak_rank}
                                  title={player.peak_rank}
                                />
                              </div>
                            </div>
                            <div className="stats-card-player">
                              <h3 className="player-name-card">
                                {player.game_name || player.valo_id?.split('#')[0] || 'Unknown'}
                              </h3>
                              <p className="team-name-card">{player.team_name}</p>
                            </div>
                            <div className="stats-card-metrics">
                              <div className="metric-row">
                                <div className="metric-item primary">
                                  <span className="metric-label">K/D</span>
                                  <span className={`metric-value ${parseFloat(kd) >= 1 ? 'positive' : 'negative'}`}>{kd}</span>
                                </div>
                                <div className="metric-item primary">
                                  <span className="metric-label">Kills</span>
                                  <span className="metric-value">{player.total_kills}</span>
                                </div>
                              </div>
                              <div className="metric-row">
                                <div className="metric-item">
                                  <span className="metric-label">Deaths</span>
                                  <span className="metric-value">{player.total_deaths}</span>
                                </div>
                                <div className="metric-item">
                                  <span className="metric-label">Assists</span>
                                  <span className="metric-value">{player.total_assists}</span>
                                </div>
                                <div className="metric-item">
                                  <span className="metric-label">Matches</span>
                                  <span className="metric-value">{player.matches_played || 0}</span>
                                </div>
                              </div>
                            </div>
                            {trackerUrl && (
                              <div className="stats-card-footer">
                                <span className="view-profile">Tap to view tracker →</span>
                              </div>
                            )}
                          </div>
                        );
                      })}
                  </div>
                ) : (
                <div className="player-perf-section">
                  {/* Redesigned filter bar */}
                  <div className="player-perf-header">
                    <div className="player-perf-title">
                      <span className="player-perf-title-accent"></span>
                      <h2>LEADERBOARD</h2>
                    </div>
                  </div>

                <div className="player-stats-table-container">
                  <div className="table-wrapper" style={{ overflowX: 'auto', maxWidth: '100%' }}>
                    <table className="player-stats-table" style={{ tableLayout: 'auto', width: '100%', minWidth: '1200px' }}>
                      <thead>
                        <tr>
                          <th style={{ width: '50px' }}
                            onClick={() => {
                              setStatsSortDirection(statsSortField === 'kills' && statsSortDirection === 'asc' ? 'desc' : 'asc');
                              setStatsSortField('kills');
                            }}
                            className={`sortable ${statsSortField === 'kills' ? `sorted-${statsSortDirection}` : ''}`}
                          >
                            No. {statsSortField === 'kills' && (statsSortDirection === 'asc' ? '↑' : '↓')}
                          </th>
                          <th 
                            onClick={() => {
                              setStatsSortDirection(statsSortField === 'name' && statsSortDirection === 'asc' ? 'desc' : 'asc');
                              setStatsSortField('name');
                            }}
                            className={`sortable ${statsSortField === 'name' ? `sorted-${statsSortDirection}` : ''}`}
                          >
                            Player {statsSortField === 'name' && (statsSortDirection === 'asc' ? '↑' : '↓')}
                          </th>
                          <th 
                            onClick={() => {
                              setStatsSortDirection(statsSortField === 'rank' && statsSortDirection === 'asc' ? 'desc' : 'asc');
                              setStatsSortField('rank');
                            }}
                            className={`sortable ${statsSortField === 'rank' ? `sorted-${statsSortDirection}` : ''}`}
                          >
                            Rank {statsSortField === 'rank' && (statsSortDirection === 'asc' ? '↑' : '↓')}
                          </th>
                          <th 
                            onClick={() => {
                              setStatsSortDirection(statsSortField === 'team' && statsSortDirection === 'asc' ? 'desc' : 'asc');
                              setStatsSortField('team');
                            }}
                            className={`sortable ${statsSortField === 'team' ? `sorted-${statsSortDirection}` : ''}`}
                            style={{ width: '15%' }}
                          >
                            Team {statsSortField === 'team' && (statsSortDirection === 'asc' ? '↑' : '↓')}
                          </th>
                          <th style={{ width: '60px' }}
                            onClick={() => {
                              setStatsSortDirection(statsSortField === 'kd' && statsSortDirection === 'asc' ? 'desc' : 'asc');
                              setStatsSortField('kd');
                            }}
                            className={`sortable ${statsSortField === 'kd' ? `sorted-${statsSortDirection}` : ''}`}
                          >
                            K/D {statsSortField === 'kd' && (statsSortDirection === 'asc' ? '↑' : '↓')}
                          </th>
                          <th 
                            onClick={() => {
                              setStatsSortDirection(statsSortField === 'kills' && statsSortDirection === 'asc' ? 'desc' : 'asc');
                              setStatsSortField('kills');
                            }}
                            className={`sortable ${statsSortField === 'kills' ? `sorted-${statsSortDirection}` : ''}`}
                          >
                            Kills {statsSortField === 'kills' && (statsSortDirection === 'asc' ? '↑' : '↓')}
                          </th>
                          <th style={{ textAlign: 'center' }}>KILLS/M</th>
                          <th 
                            onClick={() => {
                              setStatsSortDirection(statsSortField === 'deaths' && statsSortDirection === 'asc' ? 'desc' : 'asc');
                              setStatsSortField('deaths');
                            }}
                            className={`sortable ${statsSortField === 'deaths' ? `sorted-${statsSortDirection}` : ''}`}
                          >
                            Deaths {statsSortField === 'deaths' && (statsSortDirection === 'asc' ? '↑' : '↓')}
                          </th>
                          <th style={{ textAlign: 'center' }}>DEATHS/M</th>
                          <th 
                            onClick={() => {
                              setStatsSortDirection(statsSortField === 'assists' && statsSortDirection === 'asc' ? 'desc' : 'asc');
                              setStatsSortField('assists');
                            }}
                            className={`sortable ${statsSortField === 'assists' ? `sorted-${statsSortDirection}` : ''}`}
                          >
                            Assists {statsSortField === 'assists' && (statsSortDirection === 'asc' ? '↑' : '↓')}
                          </th>
                          <th style={{ textAlign: 'center' }}>ASSISTS/M</th>
                          <th 
                            onClick={() => {
                              setStatsSortDirection(statsSortField === 'matches' && statsSortDirection === 'asc' ? 'desc' : 'asc');
                              setStatsSortField('matches');
                            }}
                            className={`sortable ${statsSortField === 'matches' ? `sorted-${statsSortDirection}` : ''}`}
                          >
                            Matches {statsSortField === 'matches' && (statsSortDirection === 'asc' ? '↑' : '↓')}
                          </th>
                        </tr>
                      </thead>
                      <tbody>
                        {filteredStatsData
                          .sort((a, b) => {
                            const direction = statsSortDirection === 'asc' ? 1 : -1;
                            switch (statsSortField) {
                              case 'rank':
                                return direction * (a.rank - b.rank);
                              case 'name':
                                const nameA = a.game_name || a.valo_id?.split('#')[0] || '';
                                const nameB = b.game_name || b.valo_id?.split('#')[0] || '';
                                return direction * nameA.localeCompare(nameB);
                              case 'team':
                                return direction * (a.team_name || '').localeCompare(b.team_name || '');

                              case 'kd':
                                const kdA = (a.total_kills || 0) / Math.max(a.total_deaths || 1, 1);
                                const kdB = (b.total_kills || 0) / Math.max(b.total_deaths || 1, 1);
                                return direction * (kdA - kdB);
                              case 'kills':
                                return direction * ((a.total_kills || 0) - (b.total_kills || 0));
                              case 'deaths':
                                return direction * ((a.total_deaths || 0) - (b.total_deaths || 0));
                              case 'assists':
                                return direction * ((a.total_assists || 0) - (b.total_assists || 0));
                              case 'matches':
                                return direction * ((a.matches_played || 0) - (b.matches_played || 0));
                              default:
                                return 0;
                            }
                          })
                          .slice((currentPage - 1) * pageSize, currentPage * pageSize)
                          .map((player) => {
                            // Find matching player data to get tracker URL
                            const playerData = seasonPlayers.find((p) => p.player_id === player.player_id);
                            const trackerUrl = playerData?.tracker;

                            return (
                              <tr 
                                key={player.player_id} 
                                className={player.position <= 3 ? `position-${player.position}` : ''}
                                style={{
                                  backgroundColor: player.position === 1 ? 'rgba(255, 215, 0, 0.15)' : 
                                                 player.position === 2 ? 'rgba(192, 192, 192, 0.15)' : 
                                                 player.position === 3 ? 'rgba(205, 127, 50, 0.15)' : 'transparent'
                                }}
                                onMouseEnter={(e) => {
                                  if (!isMobileView) {
                                    const rect = e.currentTarget.getBoundingClientRect();
                                    setPopoverPosition({ x: e.clientX, y: rect.top });
                                    setHoveredPlayer(player);
                                  }
                                }}
                                onMouseLeave={() => {
                                  if (!isMobileView) {
                                    setHoveredPlayer(null);
                                  }
                                }}
                              >
                                <td className="position" style={{
                                  fontWeight: player.position <= 3 ? 'bold' : 'normal',
                                  color: player.position === 1 ? '#FFD700' :
                                        player.position === 2 ? '#C0C0C0' :
                                        player.position === 3 ? '#CD7F32' : 'inherit'
                                }}>{player.position}</td>
                                <td
                                  className={`player-name ${trackerUrl ? 'clickable' : ''}`}
                                  onClick={() => {
                                    if (trackerUrl) {
                                      window.open(trackerUrl, '_blank', 'noopener,noreferrer');
                                    }
                                  }}
                                  style={{
                                    cursor: trackerUrl ? 'pointer' : 'default',
                                    textAlign: 'left',
                                    paddingLeft: '10px',
                                    whiteSpace: 'nowrap',
                                    overflow: 'visible',
                                    textOverflow: 'clip',
                                    maxWidth: 'none',
                                    fontWeight: player.position <= 3 ? 'bold' : 'normal'
                                  }}
                                >
                                  <span style={{
                                    color: player.position === 1 ? '#FFD700' :
                                          player.position === 2 ? '#C0C0C0' :
                                          player.position === 3 ? '#CD7F32' : 'inherit'
                                  }}>{player.game_name || player.valo_id?.split('#')[0] || 'Unknown'}</span>
                                </td>
                                <td className="rank-cell" style={{ width: '50px', textAlign: 'center' }}>
                                  <img
                                    src={getRankImage(player.peak_rank)}
                                    alt={player.peak_rank}
                                    className="rank-icon"
                                    title={player.peak_rank}
                                    style={{ width: '28px', height: '28px' }}
                                  />
                                </td>
                                <td className="team-name" style={{ textAlign: 'center' }}>
                                  {player.team_name}
                                </td>

                                <td className={`kd-ratio ${(player.total_kills / Math.max(player.total_deaths, 1)) >= 1 ? 'positive' : 'negative'}`} style={{ width: '60px', textAlign: 'center' }}>
                                  {(player.total_kills / Math.max(player.total_deaths, 1)).toFixed(2)}
                                </td>
                                <td style={{ width: '60px', textAlign: 'center' }}>{player.total_kills}</td>
                                <td style={{ width: '70px', textAlign: 'center', color: 'rgba(255,255,255,0.7)' }}>
                                  {((player.total_kills || 0) / Math.max(player.matches_played || 1, 1)).toFixed(2)}
                                </td>
                                <td style={{ width: '60px', textAlign: 'center' }}>{player.total_deaths}</td>
                                <td style={{ width: '70px', textAlign: 'center', color: 'rgba(255,255,255,0.7)' }}>
                                  {((player.total_deaths || 0) / Math.max(player.matches_played || 1, 1)).toFixed(2)}
                                </td>
                                <td style={{ width: '60px', textAlign: 'center' }}>{player.total_assists}</td>
                                <td style={{ width: '70px', textAlign: 'center', color: 'rgba(255,255,255,0.7)' }}>
                                  {((player.total_assists || 0) / Math.max(player.matches_played || 1, 1)).toFixed(2)}
                                </td>
                                <td style={{ width: '60px', textAlign: 'center' }}>{player.matches_played || 0}</td>
                              </tr>
                            );
                          })}
                      </tbody>
                    </table>
                  </div>

                  {/* Pagination footer */}
                  {filteredStatsData && filteredStatsData.length > pageSize && (
                    <div className="player-perf-pagination">
                      <div className="player-perf-pagination-info">
                        SHOWING {((currentPage - 1) * pageSize) + 1}–{Math.min(currentPage * pageSize, filteredStatsData.length)} OF {filteredStatsData.length} PLAYERS
                      </div>
                      <div className="player-perf-pagination-controls">
                        <button
                          className="pp-page-btn"
                          disabled={currentPage === 1}
                          onClick={() => setCurrentPage((p) => Math.max(1, p - 1))}
                          aria-label="Previous page"
                        >‹</button>
                        {(() => {
                          const totalPages = Math.max(1, Math.ceil(filteredStatsData.length / pageSize));
                          const pages = [];
                          const window = 2;
                          const showPage = (p) => (
                            <button
                              key={`page-${p}`}
                              className={`pp-page-btn ${p === currentPage ? 'active' : ''}`}
                              onClick={() => setCurrentPage(p)}
                            >{p}</button>
                          );
                          const showDots = (k) => (
                            <span key={`dots-${k}`} className="pp-page-dots">…</span>
                          );

                          pages.push(showPage(1));
                          if (currentPage - window > 2) pages.push(showDots('left'));
                          for (let p = Math.max(2, currentPage - window); p <= Math.min(totalPages - 1, currentPage + window); p++) {
                            pages.push(showPage(p));
                          }
                          if (currentPage + window < totalPages - 1) pages.push(showDots('right'));
                          if (totalPages > 1) pages.push(showPage(totalPages));
                          return pages;
                        })()}
                        <button
                          className="pp-page-btn"
                          disabled={currentPage >= Math.ceil(filteredStatsData.length / pageSize)}
                          onClick={() => setCurrentPage((p) => Math.min(Math.ceil(filteredStatsData.length / pageSize), p + 1))}
                          aria-label="Next page"
                        >›</button>
                      </div>
                    </div>
                  )}
                </div>
                </div>
                )}
                {/* Table Legend */}
                {(!isMobileView || viewMode === 'detailed') && (
                <div className="table-legend">
                  <div className="table-legend" style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>

                    <small>
                      <strong>Rankings:</strong> 🥇 Gold | 🥈 Silver | 🥉 Bronze positions highlighted
                    </small>
                  </div>
                </div>
                )}
              </div>
            ) : !statsLoading && (!filteredStatsData || filteredStatsData.length === 0) ? (
              <div className="no-stats-message">
                <h2>{searchQuery ? 'No Matching Players Found' : 'No Player Statistics Available Right Now'}</h2>
                <p>
                  {searchQuery
                    ? 'Try adjusting your search query.'
                    : 'No player performance data found for this season.'}
                </p>
              </div>
            ) : !statsLoading ? (
              <div className="stats-placeholder">
                <h2>Player Statistics</h2>
                <p>Select a season to view player performance statistics.</p>
              </div>
            ) : null}
          </div>

          {/* Player Hover Popover */}
          {hoveredPlayer && !isMobileView && (
            <div 
              className="player-popover"
              style={{
                position: 'fixed',
                left: `${popoverPosition.x + 20}px`,
                top: `${popoverPosition.y}px`,
                zIndex: 1000
              }}
            >
              <div className="popover-header">
                <img
                  src={getRankImage(hoveredPlayer.peak_rank)}
                  alt="Rank"
                  className="popover-rank"
                />
                <div className="popover-player-info">
                  <div className="popover-name">
                    {hoveredPlayer.game_name || hoveredPlayer.valo_id?.split('#')[0] || 'Unknown'}
                  </div>
                  <div className="popover-team">{hoveredPlayer.team_name}</div>
                </div>
              </div>
              <div className="popover-stats">
                <div className="popover-stat">
                  <span className="popover-stat-label">Position</span>
                  <span className="popover-stat-value">#{hoveredPlayer.position}</span>
                </div>
                <div className="popover-stat">
                  <span className="popover-stat-label">K/D Ratio</span>
                  <span className={`popover-stat-value ${(hoveredPlayer.total_kills / Math.max(hoveredPlayer.total_deaths, 1)) >= 1 ? 'positive' : 'negative'}`}>
                    {(hoveredPlayer.total_kills / Math.max(hoveredPlayer.total_deaths, 1)).toFixed(2)}
                  </span>
                </div>
                <div className="popover-stat">
                  <span className="popover-stat-label">Kills</span>
                  <span className="popover-stat-value">{hoveredPlayer.total_kills}</span>
                </div>
                <div className="popover-stat">
                  <span className="popover-stat-label">Deaths</span>
                  <span className="popover-stat-value">{hoveredPlayer.total_deaths}</span>
                </div>
                <div className="popover-stat">
                  <span className="popover-stat-label">Assists</span>
                  <span className="popover-stat-value">{hoveredPlayer.total_assists}</span>
                </div>
                <div className="popover-stat">
                  <span className="popover-stat-label">Matches</span>
                  <span className="popover-stat-value">{hoveredPlayer.matches_played || 0}</span>
                </div>
              </div>
            </div>
          )}

          {/* Back to Top Button */}
          {showBackToTop && (
            <button 
              className="back-to-top-btn"
              onClick={scrollToTop}
              aria-label="Back to top"
            >
              <svg 
                width="24" 
                height="24" 
                viewBox="0 0 24 24" 
                fill="none" 
                stroke="currentColor" 
                strokeWidth="2" 
                strokeLinecap="round" 
                strokeLinejoin="round"
              >
                <polyline points="18 15 12 9 6 15"></polyline>
              </svg>
            </button>
          )}
        </div>
      )}
    </div>
  );
}

export default Players;